import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HotelIcon from "@mui/icons-material/Hotel";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAllClienti, creaCliente } from "../APIs/ClientsAPI";
import SidebarLayout from "../components/sidebarLayout";
import { usePermissions } from "../contexts/PermissionsContext";
import { useSnackbar } from "../contexts/SnackBarContext";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const emptyClient = {
  name: "",
  surname: "",
  email: "",
  phone: "",
  roomNumber: "",
};

const fullName = (client) => [client?.name, client?.surname].filter(Boolean).join(" ").trim();

const initialsFor = (client) => (
  [client?.name, client?.surname]
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "SP"
);

const createdClientId = (response) => response?.id || response?.client?.id || response?.data?.id;

function DetailChip({ icon, label }) {
  if (!label) return null;

  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      sx={{
        maxWidth: "100%",
        justifyContent: "flex-start",
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  );
}

export default function SpaClientsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { showSnackbar } = useSnackbar();
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(emptyClient);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const canManageClients = can("spa-clients:manage") || can("clients:manage");

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getAllClienti(token, tenant);
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(localizedErrorMessage(err, t('spa.clients.loadError')));
    } finally {
      setLoading(false);
    }
  }, [token, tenant, t]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const filteredClients = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return clients;

    return clients.filter((client) => {
      const haystack = [
        client.id,
        client.name,
        client.surname,
        client.email,
        client.phone,
        client.roomNumber,
        client.profile?.roomNumber,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [clients, query]);

  const updateForm = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!canManageClients || saving) return;
    if (!form.name.trim() || !form.surname.trim()) {
      setError(t('spa.clients.required'));
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim(),
        ...(form.email.trim() && { email: form.email.trim() }),
        ...(form.phone.trim() && { phone: form.phone.trim() }),
        ...(form.roomNumber.trim() && { roomNumber: form.roomNumber.trim() }),
      };
      const created = await creaCliente(payload, token, tenant);
      const id = createdClientId(created);

      setForm(emptyClient);
      showSnackbar(t('spa.clients.created'), "success");

      if (id) {
        navigate(`/spa/clients/${id}`);
      } else {
        await loadClients();
      }
    } catch (err) {
      setError(localizedErrorMessage(err, t('spa.clients.createError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidebarLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h3" fontWeight={900}>
                {t('spa.clients.title')}
              </Typography>
              <Chip label={t('spa.clients.loaded', { count: clients.length })} size="small" />
            </Stack>
            <Typography color="text.secondary">
              {t('spa.clients.subtitle')}
            </Typography>
          </Box>

          <Alert severity="info">
            {t('spa.clients.registry')}
          </Alert>

          {error && <Alert severity="warning">{error}</Alert>}

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper component="form" onSubmit={handleCreate} sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ManageAccountsIcon color="primary" />
                    <Typography variant="h6" fontWeight={850}>
                      {t('spa.clients.title')}
                    </Typography>
                  </Stack>

                  <TextField
                    label={t('spa.fields.name')}
                    value={form.name}
                    onChange={updateForm("name")}
                    required
                    fullWidth
                  />
                  <TextField
                    label={t('spa.fields.surname')}
                    value={form.surname}
                    onChange={updateForm("surname")}
                    required
                    fullWidth
                  />
                  <TextField
                    label={t('spa.fields.email')}
                    type="email"
                    value={form.email}
                    onChange={updateForm("email")}
                    fullWidth
                  />
                  <TextField
                    label={t('spa.fields.phone')}
                    value={form.phone}
                    onChange={updateForm("phone")}
                    fullWidth
                  />
                  <TextField
                    label={t('spa.clients.roomNumber')}
                    value={form.roomNumber}
                    onChange={updateForm("roomNumber")}
                    fullWidth
                  />

                  {!canManageClients && (
                    <Alert severity="warning">
                      {t('spa.clients.readOnly')}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <AddIcon />}
                    disabled={saving || !canManageClients}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {t('spa.clients.createAndOpen')}
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <TextField
                  label={t('spa.clients.search')}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  fullWidth
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }}
                />

                {loading ? (
                  <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
                    <CircularProgress />
                  </Paper>
                ) : error ? null : filteredClients.length ? (
                  <Grid container spacing={2}>
                    {filteredClients.map((client) => (
                      <Grid item xs={12} lg={6} key={client.id}>
                        <Paper sx={{ p: 2, height: "100%", borderRadius: 2 }}>
                          <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ bgcolor: "#0B6E99", fontWeight: 850 }}>
                                {initialsFor(client)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={850} noWrap>
                                  {fullName(client) || t('spa.clients.unnamed')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ID {client.id}
                                </Typography>
                              </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <DetailChip icon={<MailOutlineIcon />} label={client.email} />
                              <DetailChip icon={<PhoneIcon />} label={client.phone} />
                              <DetailChip icon={<HotelIcon />} label={client.profile?.roomNumber || client.roomNumber} />
                              <DetailChip icon={<BadgeOutlinedIcon />} label={client.taxCode || client.vatNumber} />
                            </Stack>

                            <Button
                              variant="outlined"
                              endIcon={<OpenInNewIcon />}
                              onClick={() => navigate(`/spa/clients/${client.id}`)}
                              sx={{ alignSelf: "flex-start" }}
                            >
                              {t('spa.clients.openProfile')}
                            </Button>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper sx={{ p: 4, borderRadius: 2 }}>
                    <Typography fontWeight={800}>{t('spa.clients.empty')}</Typography>
                    <Typography color="text.secondary">
                      {t('spa.clients.emptyHint')}
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </SidebarLayout>
  );
}
